import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { useMutation } from 'react-query';
import { useApi } from '../../../shared/context';
import { CurrentUser, DataRoomNote, DataRoomUser } from '../../../shared/models';
import { Field, Formik } from 'formik';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
	CerebroTextarea,
} from '../../../shared/components';
import { Grid } from '@mui/material';

interface AddLenderNoteFormProps {
	dataRoomGuid: string | undefined;
	user: DataRoomUser | null;
	currentUser: CurrentUser;
	close: () => void;
}

export const AddLenderNoteForm: React.FC<AddLenderNoteFormProps> = ({ dataRoomGuid, user, currentUser, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();

	const addNoteMutation = useMutation(
		async ({ values }: AddLenderNoteFormMutationVariables) => {
			const notePatch = {
				details: values.details,
				isBorrowerNote: values.isBorrowerNote,
			};
			const { data, error } = await getApiResponseData<DataRoomNote>(
				api.post(CerebroApiEndpoints.DataRoomUsers.Notes(dataRoomGuid, user?.guid), notePatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Note was added to lender.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={user != null} onClose={() => close()} PaperProps={{ sx: { width: '500px' } }}>
			<CerebroHeading>
				{user?.firstName} {user?.lastName} ({user?.bankName})
			</CerebroHeading>
			<Formik
				validationSchema={AddLenderNoteSchema}
				initialValues={{
					details: '',
					isBorrowerNote: currentUser.isCompanyUser ?? false,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(addNoteMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="details">Name</CerebroLabel>
									<Field
										id="details"
										as={CerebroTextarea}
										name="details"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('details', errors, touched)}
									/>
									{FormikUtils.IsInvalid('details', errors, touched) && (
										<CerebroText variant="error">{errors.details}</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddLenderNoteFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddLenderNoteFormValues>;
interface AddLenderNoteFormValues {
	details: string;
	isBorrowerNote: boolean;
}

const AddLenderNoteSchema = Yup.object().shape({
	details: Yup.string().required('Note details is required'),
});
